import { supabase } from "../supabase/SupabaseClient";
import { traerTodas } from "./paginar";

/* Las facturas y remitos que llegan por MAIL a cepi@fibot.ar.
 *
 * ── NO ES LA MISMA TABLA QUE LOS DE WHATSAPP ────────────────────────────────
 *
 * `comprobantes` son los que alguien de la empresa le manda a Cepi por
 * WhatsApp, y cada uno ve los suyos. `facturas` son los que manda un PROVEEDOR
 * por mail, y los ve quien tiene `ver.comprobantes.todos`. Son dos circuitos con
 * dos permisos distintos, y por eso son dos pantallas.
 *
 * LO QUE YA NO LOS SEPARA es la lectura: desde el 14/09 las dos pasan por el
 * MISMO lector (`comprobante_foto`) y guardan proveedor, CUIT, fecha, número e
 * importe en columnas con los mismos nombres. Nico pidió «lo que diga la
 * factura y el importe», y dos lectores con dos criterios habrían dado dos
 * pantallas mostrando el mismo total distinto.
 *
 * ── `proveedor` ES QUIEN LA EMITIÓ, Y `dominio` DE DÓNDE VINO ───────────────
 *
 * No son lo mismo y la diferencia se vio en la primera factura que entró: la
 * reenvió Nico desde hotmail y la columna decía «hotmail.com». La factura era
 * de Würth Argentina SA. Agrupar por dominio mete a Würth, a la gomería y al
 * corralón adentro de «hotmail.com».
 *
 * ── EL FILTRO ES DE LA BASE ─────────────────────────────────────────────────
 *
 * `facturas_lectura` pide `ver.comprobantes.todos`. Sin la llave la lista vuelve
 * vacía, y eso se lee igual que «todavía no llegó ninguna» — por eso la
 * pantalla mira el permiso antes de mostrar la lista y dice cuál es la
 * diferencia.
 *
 * ── DE QUÉ TIPO ES CADA UNA ─────────────────────────────────────────────────
 *
 * Lo dice el papel, leído; el asunto que escribió el que la mandó es el
 * respaldo mientras la lectura no llegó. Lo que no lo dice ninguno de los dos
 * cae en «sin clasificar», no en «otro» ni en «factura» por descarte.
 */

export const BUCKET = "facturas";

/* Una hora, igual que el visor de vales y el de comprobantes: el que abre la
 * pantalla lo ve y el link pegado en un chat se muere solo. Una factura trae
 * CUIT, importes y condiciones de pago. */
export const VIGENCIA = 3600;

/* `lectura_error` y `leido_en` NO son opcionales en esta lista: sin los dos, la
   pantalla no puede distinguir «todavía no se leyó» de «se leyó y no se pudo»,
   y las dos se dibujan igual —los campos vacíos— siendo opuestas. */
const CAMPOS =
  "id,remitente,remitente_email,dominio,asunto,fecha_mail,nombre,mime,ruta," +
  "bytes,estado,nota,creado_en,usado_en," +
  "tipo,proveedor,cuit,fecha,numero,importe,moneda,detalle,dudas," +
  "leido_por,leido_en,lectura_error";


/* Todas las que la base me deje ver.
 *
 * `traerTodas` y no un `.limit()`: hoy hay una y el tope no se toca, pero el
 * día que sean mil una lista cortada se ve igual que una completa — y lo que
 * faltaría son facturas. Es el mismo agujero que ya mordió en Presidencia.
 *
 * Desempata por `id` porque `fecha_mail` empata: un proveedor que manda cuatro
 * adjuntos en un mail deja cuatro filas con el mismo segundo, y paginar con un
 * orden que empata repite una fila y saltea otra.
 */
export function traerFacturas() {
  return traerTodas(() =>
    supabase
      .from("facturas")
      .select(CAMPOS, { count: "exact" })
      .order("fecha_mail", { ascending: false, nullsFirst: false })
      .order("id", { ascending: false }),
  );
}

/* El link firmado al papel. `null` es «no se pudo», y la pantalla lo dice. */
export async function archivoUrl(ruta) {
  if (!ruta) return null;
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(ruta, VIGENCIA);
  if (error) return null;
  return data?.signedUrl || null;
}

/* Marcarla usada / volver a dejarla nueva. Sólo `estado` y `usado_en`: lo que
 * dice el mail no se edita desde una pantalla — un campo de origen que alguien
 * puede pisar deja de ser evidencia.
 *
 * Hasta el 20/10 este comentario decía que la base concedía sólo esas
 * columnas, y era falso: el `grant update (estado, nota, usado_en)` de
 * 20260914182818 no recortaba nada, porque authenticated tenía UPDATE sobre la
 * tabla entera (el default de Supabase) y ese grant lo pisaba. Desde
 * 20261020025324_proveedores_facturas_a_pagar la tabla ya no se concede y
 * quedan sólo `estado`, `nota` y `usado_en`: cambiar el importe, el CUIT o el
 * número de un mail por la API da «permission denied».
 *
 * «Usada» NO es «pagada»: el pago se controla en Proveedores › Facturas a
 * pagar. */
export async function marcar(id, usada) {
  const { error } = await supabase
    .from("facturas")
    .update({
      estado: usada ? "usada" : "nuevo",
      usado_en: usada ? new Date().toISOString() : null,
    })
    .eq("id", id);
  if (error) throw error;
}
